import React from "react";
import { useLocation } from "react-router-dom";
import QRCode from "qrcode.react";

import { ServiceProviderR } from "../routes";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import {
  MatrixCodeContextProps,
  MatrixCodeService,
} from "../../../../application/service/graffer-utility-tools/matrix-code/matrix-code-service";
import { useModal } from "../../../../utils/custom-hooks";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { DownloadMatrixCodeModal } from "../../graffer-utility-tools/modals/download-matrix-code-modal";

const S_SIZE = 128;
const M_SIZE = 256;
const L_SIZE = 512;

const MatrixCodePage: React.FC<MatrixCodeContextProps> = ({
  inputUrl,
  isValidInput,
  qrUrl,
  displayCode,
  fileExtension,
  update,
  updateFileExtension,
  onGenerateMatrixCode,
  onDownloadMatrixCode,
}) => {
  const downloadMatrixCodeModal = useModal();
  const [selectedSize, setSelectedSize] = React.useState(M_SIZE);
  const [size, setSize] = React.useState(M_SIZE);
  const [selectedIncludeMargin, setSelectedIncludeMargin] =
    React.useState(false);
  const [includeMargin, setIncludeMargin] = React.useState(false);
  const { queryUrl } = parseQueryOfLocation(useLocation()) as {
    queryUrl: string | undefined;
  };
  const displayError = !isValidInput && inputUrl !== "";
  React.useEffect(() => {
    if (queryUrl) {
      const decodedUrl = decodeURIComponent(queryUrl);
      update(decodedUrl, decodedUrl, true);
    }
  }, [queryUrl, update]);

  const breadcrumbs = [
    { to: ServiceProviderR.Home.link({}), title: "テナント一覧" },
  ];

  return (
    <ServiceProviderTemplate breadcrumbs={breadcrumbs}>
      <h1 className="title">
        <i className="fas fa-qrcode" /> 二次元コード生成
      </h1>
      <section className="section px-0 py-0">
        <div className="title is-size-4 mb-4">データ入力</div>
        <div className="field">
          <label className="label">URL</label>
          <input
            className={`input ${displayError ? "is-danger" : ""}`}
            type="url"
            placeholder="https://graffer.jp"
            value={inputUrl}
            onChange={(v) => update(v.target.value, qrUrl, displayCode)}
            disabled={!!queryUrl}
          />
          {displayError && (
            <p className="help is-danger">正しいURLを入力してください。</p>
          )}
        </div>
        <div className="field">
          <label className="label">サイズ</label>
          <div className="my-2">
            <label className="radio">
              <input
                type="radio"
                name="matrix-code-size"
                className="mr-1"
                checked={selectedSize === S_SIZE}
                onChange={() => setSelectedSize(S_SIZE)}
              />
              S(128px)
            </label>
            <label className="radio">
              <input
                type="radio"
                name="matrix-code-size"
                className="ml-2 mr-1"
                checked={selectedSize === M_SIZE}
                onChange={() => setSelectedSize(M_SIZE)}
              />
              M(256px)
            </label>
            <label className="radio">
              <input
                type="radio"
                name="matrix-code-size"
                className="ml-2 mr-1"
                checked={selectedSize === L_SIZE}
                onChange={() => setSelectedSize(L_SIZE)}
              />
              L(512px)
            </label>
          </div>
        </div>
        <div className="field">
          <label className="label">余白</label>
          <div className="my-2">
            <label className="radio">
              <input
                type="radio"
                name="include-margin"
                className="mr-1"
                checked={!selectedIncludeMargin}
                onChange={() => setSelectedIncludeMargin(false)}
              />
              なし
            </label>
            <label className="radio">
              <input
                type="radio"
                name="include-margin"
                className="ml-2 mr-1"
                checked={selectedIncludeMargin}
                onChange={() => setSelectedIncludeMargin(true)}
              />
              あり
            </label>
          </div>
        </div>
        <div className="field">
          <label className="label">拡張子</label>
          <div className="my-2">
            <label className="radio">
              <input
                type="radio"
                name="file-extension"
                className="mr-1"
                checked={fileExtension === "jpeg"}
                onChange={() => updateFileExtension("jpeg")}
              />
              jpeg
            </label>
            <label className="radio">
              <input
                type="radio"
                name="file-extension"
                className="ml-2 mr-1"
                checked={fileExtension === "png"}
                onChange={() => updateFileExtension("png")}
              />
              png
            </label>
          </div>
        </div>
        <p className="control">
          <button
            className={`button ${displayCode ? "" : "is-info"}`}
            disabled={displayError}
            onClick={() => {
              setSize(selectedSize);
              setIncludeMargin(selectedIncludeMargin);
              onGenerateMatrixCode();
            }}
          >
            二次元コードを{displayCode ? "更新" : "生成"}する
          </button>
        </p>
      </section>
      {displayCode && (
        <section className="section px-0">
          <div className="title is-size-4 mb-4">二次元コード</div>
          <div
            style={{
              border: `${includeMargin ? "1px solid #333" : ""}`,
              display: "inline-block",
              width: `${size + 2}px`,
              height: `${size + 2}px`,
            }}
          >
            <QRCode
              value={qrUrl}
              renderAs="canvas"
              includeMargin={includeMargin}
              size={size}
            />
          </div>
          <p className="control mt-4">
            <button
              className="button is-info disabled"
              onClick={downloadMatrixCodeModal.open}
              disabled={!displayCode}
            >
              ダウンロード
            </button>
          </p>
        </section>
      )}
      <DownloadMatrixCodeModal
        isActive={downloadMatrixCodeModal.isActive}
        href={qrUrl}
        onDownloadMatrixCode={onDownloadMatrixCode}
        onModal={downloadMatrixCodeModal.close}
      />
    </ServiceProviderTemplate>
  );
};

export const MatrixCodePageContainer = connectPageService(
  MatrixCodePage,
  () => new MatrixCodeService(),
);
